import FlyoutMenu from '@/components/layout/FlyoutMenu'
import {
  ASSET_SET_TYPE_LABEL_SHORT,
  ASSET_SET_TYPE_TONE,
} from '@/lib/assetSetDisplay'
import { captureDisplayDate } from '@/lib/captureTime'
import { userDisplayName, userInitials } from '@/lib/userDisplay'
import { cn, formatBytes } from '@/lib/utils'
import {
  ArrowDownTrayIcon,
  LinkIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/20/solid'
import { ArrowRightIcon, PhotoIcon } from '@heroicons/react/24/outline'
import { Link } from '@tanstack/react-router'

import type {
  AssetSet,
  AssetSetSummary,
} from '@droidmap/asset-service-contract'
import { Avatar } from '@droidmap/frontend-ui'
import { Badge } from '@droidmap/frontend-ui'
import { CopyId } from '@droidmap/frontend-ui'
import { MenuItem } from '@droidmap/frontend-ui'
import { StorageSummary } from '@droidmap/shared-service-contract'
import { User } from '@droidmap/user-service-contract'

const totalStorageBytes = (s: StorageSummary | undefined): number => {
  if (!s) return 0
  return (Object.values(s) as unknown[]).reduce<number>(
    (sum, v) => sum + (typeof v === 'number' ? v : 0),
    0,
  )
}

const formatDate = (d: Date | string): string =>
  new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })

/**
 * The set's capture date in the *photographer's* clock — the day the flight
 * happened, identical for every viewer. Localising the underlying instant
 * instead would land a dawn or late-evening flight on the neighbouring day for
 * anyone in another timezone, and this card is a day/month stat with no year
 * or time to give the slip away.
 */
const formatCapturedAt = (assetSet: AssetSet): string => {
  const d = captureDisplayDate(
    assetSet.capturedAt,
    assetSet.capturedAtOffsetMinutes,
  )
  return d ? formatDate(d) : '—'
}

export interface AssetSetCardProps {
  assetSet: AssetSet
  /**
   * Card summary (representative thumbnail). Undefined → still loading or the
   * set has no thumbnailed imagery, either way the placeholder grid renders.
   */
  summary?: AssetSetSummary
  /** Creator user record (looked up via the org's userMap). */
  creator?: User
  /** Org id used to build the link target. */
  orgId: string
  /** Project id used to build the link target. */
  projectId: string
  /** Fired when the user picks Download from the card kebab. */
  onDownload: (assetSet: AssetSet) => void
  /** Fired when the user picks Delete from the card kebab. */
  onDelete: (assetSet: AssetSet) => void
  /** Fired when the user picks Edit from the card kebab. */
  onEdit?: (assetSet: AssetSet) => void
  /** Fired when the user picks Share from the card kebab. */
  onShare?: (assetSet: AssetSet) => void
}

/**
 * Card thumbnail. Renders the set's representative image when one is available
 * (from the asset-set summary), otherwise the placeholder coordinate-grid
 * background with a centred photo glyph — mirrors the project card so the two
 * grids read as one system.
 */
function AssetSetThumb({ thumbnailUrl }: { thumbnailUrl?: string }) {
  if (thumbnailUrl) {
    return (
      <img
        src={thumbnailUrl}
        alt=""
        aria-hidden
        loading="lazy"
        className="h-full w-full object-cover"
      />
    )
  }

  return (
    <div className="bg-bg-2 relative grid h-full w-full place-items-center">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 [background-image:linear-gradient(var(--grid-visible)_1px,transparent_1px),linear-gradient(90deg,var(--grid-visible)_1px,transparent_1px)] [background-size:20px_20px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)] [-webkit-mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]"
      />
      <PhotoIcon
        aria-hidden
        className="text-text-mute relative z-[1] h-10 w-10"
      />
    </div>
  )
}

function Stat({
  value,
  label,
  muted,
  divider,
}: {
  value: React.ReactNode
  label: string
  muted?: boolean
  divider?: boolean
}) {
  return (
    <div
      className={cn(
        'relative text-center',
        divider &&
          "before:bg-border before:absolute before:bottom-[25%] before:left-0 before:top-[25%] before:w-px before:content-['']",
      )}
    >
      <div
        className={cn(
          'font-mono text-[16px] font-medium leading-none tracking-tight',
          muted ? 'text-text-mute' : 'text-text',
        )}
      >
        {value}
      </div>
      <div className="text-micro text-text-mute mt-1.5">{label}</div>
    </div>
  )
}

/**
 * Meridian asset-set card. The grid-view counterpart to {@link AssetSetTable}:
 * renders the representative thumbnail (or a placeholder grid when none
 * exists), a type pill (Drone / ODM / …), name, ULID, a three-stat strip
 * (Assets / Size / Captured), and a footer with creator + "Open →". Mirrors the
 * project card so both grids read as one system.
 */
export function AssetSetCard({
  assetSet,
  summary,
  creator,
  orgId,
  projectId,
  onDownload,
  onDelete,
  onEdit,
  onShare,
}: AssetSetCardProps) {
  const size = totalStorageBytes(assetSet.storageSummary)
  const assets = assetSet.assetCount ?? 0
  const isEmpty = assets === 0
  const tone = ASSET_SET_TYPE_TONE[assetSet.assetSetType]

  return (
    <Link
      to="/orgs/$orgId/projects/$projectId/asset-sets/$assetSetId"
      params={{ orgId, projectId, assetSetId: assetSet.id }}
      className={cn(
        'group/card bg-panel border-border relative flex flex-col overflow-hidden rounded-lg border',
        'transition-[transform,border-color,box-shadow] duration-b ease-meridian',
        'hover:border-accent-tint hover:shadow-elevated hover:-translate-y-0.5',
        'focus-visible:border-accent-tint focus-visible:ring-accent-soft focus-visible:outline-hidden focus-visible:ring-2',
      )}
    >
      <div className="border-border bg-bg-2 relative h-[170px] shrink-0 overflow-hidden border-b">
        {/* Type badge */}
        <Badge
          size="sm"
          tone={tone}
          className="absolute left-2.5 top-2.5 z-[2] backdrop-blur-xs"
        >
          {ASSET_SET_TYPE_LABEL_SHORT[assetSet.assetSetType]}
        </Badge>

        {/* Hover actions kebab */}
        <div
          className="absolute right-2.5 top-2.5 z-[2] opacity-0 transition-opacity duration-150 group-hover/card:opacity-100"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          <FlyoutMenu buttonClassName="border-border-strong bg-bg-2/80 text-text-dim hover:text-text hover:border-accent-tint grid h-7 w-7 place-items-center rounded-xs border backdrop-blur-md transition-colors duration-150">
            {onEdit && (
              <MenuItem
                icon={<PencilIcon className="w-4 h-4" />}
                onClick={() => onEdit(assetSet)}
              >
                Edit
              </MenuItem>
            )}
            <MenuItem
              icon={<ArrowDownTrayIcon className="w-4 h-4" />}
              onClick={() => onDownload(assetSet)}
            >
              Download
            </MenuItem>
            {onShare && (
              <MenuItem
                icon={<LinkIcon className="w-4 h-4" />}
                onClick={() => onShare(assetSet)}
              >
                Share
              </MenuItem>
            )}
            <MenuItem
              icon={<TrashIcon className="w-4 h-4" />}
              tone="danger"
              onClick={() => onDelete(assetSet)}
            >
              Delete
            </MenuItem>
          </FlyoutMenu>
        </div>

        <AssetSetThumb thumbnailUrl={summary?.thumbnailPresignedUrl} />
      </div>

      <div className="flex-1 px-[18px] py-3.5">
        <h3 className="text-text mb-1 truncate text-[15px] font-semibold leading-tight tracking-tight">
          {assetSet.name}
        </h3>
        <CopyId id={assetSet.id} className="mb-3" />
        <div className="border-border -mx-[18px] grid grid-cols-3 border-y py-3">
          <Stat value={assets} label="Assets" muted={isEmpty} />
          <Stat
            value={formatBytes(size, 1)}
            label="Size"
            muted={isEmpty}
            divider
          />
          <Stat
            value={formatCapturedAt(assetSet)}
            label="Captured"
            muted={!assetSet.capturedAt}
            divider
          />
        </div>
      </div>

      <div className="border-border bg-bg-2 flex items-center justify-between border-t px-[18px] py-3">
        <div className="text-text-dim flex items-center gap-2 font-mono text-[11px]">
          <Avatar size="sm" color="violet" initials={userInitials(creator)} />
          <span className="text-text-dim">{userDisplayName(creator)}</span>
          {assetSet.created && (
            <span className="text-text-mute">
              · {formatDate(assetSet.created)}
            </span>
          )}
        </div>
        <span className="text-text-mute group-hover/card:text-accent inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider transition-colors">
          Open
          <ArrowRightIcon className="duration-f ease-meridian h-3.5 w-3.5 transition-transform group-hover/card:translate-x-0.5" />
        </span>
      </div>
    </Link>
  )
}

export default AssetSetCard
